import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Tooltip } from "@mui/material";
import ControlFilter from "components/shared/ControlFilter";
import DateRangeSelectors from "components/shared/DateRangeSelectors";
import LastElementTable from "components/shared/LastElementTable";
import type { LastElementTableAPIData, LastElementTableColumns } from "components/shared/LastElementTable/type";
import ModalBase from "components/shared/ModalBase";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import type { InputType } from "helpers/utils";
import {
    formatLocaleDateString,
    getCleanStartorEndDay,
    isObject,
    isObjectEmpty,
    renderLogPayloadData,
} from "helpers/utils";
import useAPIPagination from "hooks/useAPIPagination";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import type { RootState } from "store/store";
import type { IDatesRange, ILog } from "types";

const LogList = () => {
    const [t] = useTranslation();
    const { logList, countLog } = useSelector((state: RootState) => state.adminPanel);
    const { companyInfos } = useCompanySettings();
    const {
        query,
        handleQuery,
        requestStatus,
        method,
        setMethod,
        dates,
        setDates,
        page,
        limitPagination,
        handleChangePage,
        handleChangeLimit,
        offsetPagination,
    } = useAPIPagination({
        type: "logs",
        sortOrder: "desc",
    });
    const [methodOption, setMethodOption] = useState(method ?? "all");
    const [modalType, setModalType] = useState("");
    const [payload, setPayload] = useState({} as ILog["payload"]);

    const APIData = {
        query,
        handleQuery,
        page,
        count: countLog,
        limitPagination,
        handleChangePage,
        handleChangeLimit,
    } as unknown as LastElementTableAPIData;

    const [dateRange, setDateRange] = useState<IDatesRange>({
        startDate: getCleanStartorEndDay(companyInfos?.created),
        endDate: getCleanStartorEndDay(new Date(), { time: "end" }),
    });

    const handleMethodFilter = (type) => {
        setMethod(type);
        setMethodOption(type);
    };

    const handleDatesFilter = (datesChosen: IDatesRange | undefined) => {
        if (!datesChosen) return;
        setDateRange(datesChosen);
        setDates(datesChosen);
    };

    const logsLoading = ["idle", "pending"].includes(requestStatus);

    const handlePayloadModal = (log: ILog) => {
        if (!log) return;
        setPayload(log.payload);
        setModalType("payload");
    };

    const columns: LastElementTableColumns[] = [
        { id: "id", align: "right", label: "ID", format: (value: number) => `#${value}` },
        { id: "email", align: "left", label: t("Beneficiars.Email") },
        {
            id: "created",
            align: "left",
            label: t("Payment.Date"),
            format: (value: "string") =>
                formatLocaleDateString({ date: value, options: { numeric: true, showTime: true, timeZone: null } }),
        },
        { id: "method", align: "left", label: t("AdminPanel.Method") },
        {
            id: "endpoint",
            align: "left",
            label: t("AdminPanel.Endpoint"),
            format: (value: string) => (
                <Tooltip title={value}>
                    <div className="log-endpoint">{value}</div>
                </Tooltip>
            ),
        },
        { id: "ipAddress", align: "left", label: t("AdminPanel.IpAdress") },
        {
            id: "payload",
            label: "Payload",
            format: (value, _, log: ILog) =>
                log.method !== "GET" ? (
                    <VisibilityIcon sx={{ width: "16px", cursor: "pointer" }} onClick={() => handlePayloadModal(log)} />
                ) : (
                    ""
                ),
        },
    ];

    const methodFilterOptions = [
        { value: "all", label: "All" },
        { value: "GET", label: "GET" },
        { value: "POST", label: "POST" },
        { value: "PUT", label: "PUT" },
        { value: "DELETE", label: "DELETE" },
    ];

    const isPayloadFilled = useMemo(() => !isObjectEmpty(payload), [payload]);

    return (
        <div className="log-list-container">
            {isPayloadFilled && (
                <ModalBase
                    open={modalType === "payload"}
                    onClose={() => setModalType("")}
                    modalTitle="Détail du payload"
                >
                    <div
                        style={{
                            maxHeight: "500px",
                            overflow: "auto",
                            display: "flex",
                            flexDirection: "column",
                            gap: "10px",
                        }}
                    >
                        {renderLogPayloadData(payload as unknown as InputType)}
                    </div>
                </ModalBase>
            )}
            <div className="admin-search">
                <StyledTextField
                    placeholder="Search for an email or endpoint"
                    value={query}
                    size="small"
                    onChange={handleQuery}
                    InputProps={{
                        endAdornment: <SearchIcon sx={{ color: "#A8A8A8", width: "15px" }} />,
                    }}
                />
                <ControlFilter
                    filterLabel="Méthode"
                    filterOptions={methodFilterOptions}
                    controlValue={methodOption}
                    onValueChange={handleMethodFilter}
                />
                <DateRangeSelectors
                    label={"Date : "}
                    dateRange={dates}
                    handleChanges={handleDatesFilter}
                    defaultValues={dateRange}
                />
            </div>
            <LastElementTable
                columns={columns}
                data={logList}
                dataType="logs"
                onShowAll={() => {}}
                max={-1}
                isLoading={logsLoading}
                columnsNumber={7}
                APIData={APIData}
                dataCount={APIData?.count}
            />
        </div>
    );
};

export default LogList;
